import React from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';
import { fadeUp, stagger } from '@/lib/motion';
import { Section, SectionTitle } from '@/components/shared/Section';
import { ArrowRight, Gauge, Briefcase, TrendingUp, TrendingDown, AlertTriangle, Activity } from 'lucide-react';

const BASE_URL = window.location.origin;

const demos = [
  {
    id: 'ceocockpit',
    label: 'CEO Cockpit™ (Demo)',
    icon: Gauge,
    description: 'KPIs ejecutivos: operación, ventas, merma, uptime, alertas críticas.',
    url: `${BASE_URL}/ceocockpit`,
    href: '/ceocockpit',
    color: '#0EA5E9',
    predictions: [
      {
        icon: TrendingUp,
        title: 'Demanda autoservicio Q3',
        prediction: '+23% de adopción self-checkout estimada para el próximo trimestre en zona RM.',
        certainty: 87,
        impact: 'Alto',
        impactColor: '#10B981',
        source: 'Modelo predictivo ALLCOM · Datos históricos 24 meses · 1.2M transacciones analizadas',
      },
      {
        icon: AlertTriangle,
        title: 'Riesgo de merma estacional',
        prediction: 'Probabilidad de incremento de merma en +18% durante temporada alta (Nov-Dic).',
        certainty: 79,
        impact: 'Crítico',
        impactColor: '#EF4444',
        source: 'Análisis estacional ALLCOM IA · Patrones de 3 ciclos anteriores · 420 puntos de venta',
      },
    ],
  },
  {
    id: 'csuite',
    label: 'C-Suite Dashboard™ (Demo)',
    icon: Briefcase,
    description: 'Vista por área: Operaciones, TI, Finanzas y Experiencia del Cliente.',
    url: `${BASE_URL}/csuite`,
    href: '/csuite',
    color: '#8B5CF6',
    predictions: [
      {
        icon: TrendingDown,
        title: 'Optimización OPEX proyectada',
        prediction: 'Migración a arriendo reduce costos operativos en -31% en 12 meses.',
        certainty: 91,
        impact: 'Alto',
        impactColor: '#10B981',
        source: 'Simulación financiera ALLCOM · Benchmark 180 empresas · Modelo TCO validado',
      },
      {
        icon: Activity,
        title: 'Predicción de fallas IoT',
        prediction: '14 dispositivos con probabilidad de falla >70% en los próximos 15 días.',
        certainty: 83,
        impact: 'Preventivo',
        impactColor: '#F59E0B',
        source: 'Motor predictivo ALLCOM · Telemetría IoT en tiempo real · 8.400 dispositivos monitoreados',
      },
    ],
  },
];

const trackClick = (demoId) => {
  if (window.dataLayer) {
    window.dataLayer.push({ event: `click_qr_${demoId}`, page_path: window.location.pathname, element_location: 'qr_section', destination: `/${demoId}` });
  }
};

const CertaintyBar = ({ value, color }) => (
  <div className="flex items-center gap-2 mt-1.5">
    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${value}%`, backgroundColor: color }} />
    </div>
    <span className="text-[10px] font-bold" style={{ color, fontFamily: 'JetBrains Mono, monospace' }}>{value}%</span>
  </div>
);

const QRDemoSection = () => (
  <Section data-testid="qr-demo-section">
    <SectionTitle
      badge="PRUEBA EN VIVO"
      title="Pruébalo en tu celular"
      subtitle="Escanea un QR y mira cómo se vería tu operación en un dashboard ejecutivo."
      center
    />
    <motion.div variants={stagger} className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
      {demos.map((demo) => {
        const Icon = demo.icon;
        return (
          <motion.div key={demo.id} variants={fadeUp}>
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.08)] card-hover">
              {/* Top: QR + Info */}
              <div className="text-center mb-6">
                <div className="inline-block p-4 rounded-2xl border-2 border-dashed mb-4" style={{ borderColor: `${demo.color}30` }}>
                  <QRCodeSVG value={demo.url} size={140} level="M" fgColor="#0A2540" bgColor="transparent" className="mx-auto" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Icon className="w-5 h-5" style={{ color: demo.color }} />
                  <h3 className="text-base font-semibold" style={{ fontFamily: 'Syne, sans-serif' }}>{demo.label}</h3>
                </div>
                <p className="text-xs text-[#64748B] leading-relaxed">{demo.description}</p>
              </div>

              {/* Predictions */}
              <div className="space-y-3 mb-6">
                <p className="text-[9px] font-semibold text-[#94A3B8] uppercase tracking-[0.18em] text-center">Predicciones inteligentes</p>
                {demo.predictions.map((pred, i) => {
                  const PredIcon = pred.icon;
                  return (
                    <div key={i} className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex items-start gap-3">
                        <PredIcon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: pred.impactColor }} strokeWidth={2} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h4 className="text-xs font-semibold text-[#0A2540]">{pred.title}</h4>
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold shrink-0" style={{ backgroundColor: `${pred.impactColor}15`, color: pred.impactColor }}>
                              {pred.impact}
                            </span>
                          </div>
                          <p className="text-[11px] text-[#64748B] leading-relaxed mb-1.5">{pred.prediction}</p>
                          <CertaintyBar value={pred.certainty} color={demo.color} />
                          <p className="text-[9px] text-[#94A3B8] mt-1.5 leading-relaxed">{pred.source}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <Link
                to={demo.href}
                onClick={() => trackClick(demo.id)}
                className="flex items-center justify-center gap-2 w-full px-6 py-3 text-sm font-semibold text-white rounded-xl transition-all hover:-translate-y-px shadow-lg"
                style={{ backgroundColor: demo.color }}
                data-testid={`qr-open-${demo.id}`}
              >
                Abrir {demo.label.split(' (')[0]} <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-[9px] text-slate-400 mt-2 text-center">Demo pública — datos simulados</p>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  </Section>
);

export default QRDemoSection;
